import { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import {
  LayoutDashboard, FolderKanban, CheckSquare, MessageSquare,
  Film, Settings, X, ChevronRight, ChevronLeft,
  HardDrive, FileText, Palette, Smartphone, Bell,
} from 'lucide-react'

const STEPS_MEMBER = [
  {
    icon: LayoutDashboard,
    titolo: 'Dashboard',
    descrizione: 'La tua centrale di controllo. Vedi i progetti attivi, le task urgenti e le scadenze della settimana. Tutto a colpo d\'occhio.',
    color: 'text-violet-400',
    bg: 'bg-violet-500/10',
  },
  {
    icon: FolderKanban,
    titolo: 'Progetti',
    descrizione: 'Crea e gestisci i tuoi progetti. Aggiungi membri con ruoli specifici (editor, viewer, stagista), traccia lo stato e monitora le scadenze. Ogni progetto ha il suo colore e la sua timeline.',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
  },
  {
    icon: CheckSquare,
    titolo: 'Tasks',
    descrizione: 'Sistema di task con priorità, assegnazione, subtask e status (todo / in corso / fatto). Puoi creare task personali o legarle a un progetto. All\'interno di un progetto puoi anche suddividere i task per scena.',
    color: 'text-green-400',
    bg: 'bg-green-500/10',
  },
  {
    icon: Film,
    titolo: 'Revisioni',
    descrizione: 'Invia contenuti (video, immagini, PDF) ai clienti per l\'approvazione. Il cliente vede il materiale con watermark, può lasciare commenti con timecode e approvare o richiedere modifiche.',
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
  },
  {
    icon: FileText,
    titolo: 'Preventivi',
    descrizione: 'Crea preventivi professionali in pochi secondi. Descrivi il progetto in parole tue e l\'AI genera automaticamente le voci con i prezzi calibrati sulle tue tariffe. Ogni preventivo include IVA automatica, download PDF con logo e ritenuta d\'acconto se sei in forfettario.',
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
  },
  {
    icon: MessageSquare,
    titolo: 'Chat',
    descrizione: 'Messaggistica diretta con i tuoi collaboratori e clienti. Badge in tempo reale per i messaggi non letti. Il bot ti avvisa anche su scadenze e approvazioni.',
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
  },
  {
    icon: Palette,
    titolo: 'Strumenti creativi',
    descrizione: 'Moodboard per raccogliere ispirazioni (con drag & drop e touch su mobile), Storage per i file di progetto, Note veloci e Calendario per le scadenze. Tutto accessibile anche da smartphone.',
    color: 'text-fuchsia-400',
    bg: 'bg-fuchsia-500/10',
  },
  {
    icon: Smartphone,
    titolo: 'Tutto su mobile',
    descrizione: 'L\'app è completamente ottimizzata per smartphone. Usa la barra in basso per navigare: Home, Progetti, Tasks e Chat. Tocca "Altro" per accedere a tutte le sezioni, incluse quelle admin.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
  },
  {
    icon: Bell,
    titolo: 'Notifiche',
    descrizione: 'La campanella in alto a destra ti avvisa in tempo reale: task assegnate, revisioni ricevute, commenti, aggiunta a un progetto. Clicca su una notifica per andare direttamente alla sezione relativa.',
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
  },
  {
    icon: Settings,
    titolo: 'Impostazioni',
    descrizione: 'Configura il tuo profilo creativo e i dati fiscali: P.IVA, regime fiscale, logo studio, tariffe orarie e servizi tipici. Se sei in forfettario c\'è un calcolatore delle tasse integrato. Le tariffe che inserisci vengono usate dall\'AI per generare preventivi calibrati su di te.',
    color: 'text-gray-400',
    bg: 'bg-gray-500/10',
  },
]

const STEPS_CLIENT = [
  {
    icon: Film,
    titolo: 'Revisioni',
    descrizione: 'Qui trovi tutti i materiali che il team ti invia per l\'approvazione. Puoi guardare video e immagini, lasciare commenti (anche con timecode per i video) e approvare o richiedere modifiche.',
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
  },
  {
    icon: FileText,
    titolo: 'Preventivi',
    descrizione: 'Visualizza i preventivi inviati dallo studio. Puoi accettarli con un click o richiedere modifiche lasciando un messaggio al team.',
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
  },
  {
    icon: HardDrive,
    titolo: 'File',
    descrizione: 'Accedi ai file caricati dallo studio per i tuoi progetti: video finali, immagini, documenti. Puoi scaricarli o visualizzarli direttamente nell\'app.',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
  },
  {
    icon: MessageSquare,
    titolo: 'Chat',
    descrizione: 'Scrivi direttamente al team di Plutonic Studio. Ricevi notifiche in tempo reale per ogni risposta.',
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
  },
  {
    icon: Bell,
    titolo: 'Notifiche',
    descrizione: 'La campanella in alto a destra ti avvisa quando arriva una nuova revisione da approvare, un commento o una risposta in chat. Non perdere nessun aggiornamento dal team.',
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
  },
]

// ─── OnboardingTour ────────────────────────────────────────────────────────────
const OnboardingTour = ({ forceOpen = false, onClose: externalClose }) => {
  const { profile, refreshProfile } = useAuth()
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(0)

  const isClient = profile?.role === 'client'
  const steps = isClient ? STEPS_CLIENT : STEPS_MEMBER

  useEffect(() => {
    if (forceOpen) {
      setStep(0)
      setOpen(true)
      return
    }
    // Mostra automaticamente solo se non completato
    if (profile && profile.onboarding_completato === false) {
      const timer = setTimeout(() => setOpen(true), 800)
      return () => clearTimeout(timer)
    }
  }, [profile, forceOpen])

  const completa = async () => {
    await supabase.from('profiles').update({ onboarding_completato: true }).eq('id', profile.id)
    await refreshProfile()
    setOpen(false)
    if (externalClose) externalClose()
  }

  const handleClose = () => {
    setOpen(false)
    if (externalClose) externalClose()
    if (!profile?.onboarding_completato) completa()
  }

  if (!open) return null

  const current = steps[step]
  const Icon = current.icon
  const isFirst = step === 0
  const isLast = step === steps.length - 1

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <span className="text-xs text-gray-500">
            {step + 1} / {steps.length}
          </span>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-white transition-colors"
            title="Salta il tour"
          >
            <X size={16} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-0.5 bg-gray-800">
          <div
            className="h-full bg-violet-500 transition-all duration-300"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-8 text-center">
          <div className={`w-20 h-20 rounded-2xl ${current.bg} border border-gray-800 flex items-center justify-center mx-auto mb-5`}>
            <Icon size={36} className={current.color} />
          </div>
          <h2 className="text-xl font-bold text-white mb-3">{current.titolo}</h2>
          <p className="text-gray-400 leading-relaxed text-sm">{current.descrizione}</p>
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between px-5 pb-5 gap-3">
          <button
            onClick={() => setStep(s => s - 1)}
            disabled={isFirst}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-800 transition-colors disabled:opacity-0 disabled:pointer-events-none"
          >
            <ChevronLeft size={16} /> Indietro
          </button>

          {isLast ? (
            <button
              onClick={completa}
              className="flex-1 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold text-sm transition-colors"
            >
              Inizia a usare Plutonic 🚀
            </button>
          ) : (
            <button
              onClick={() => setStep(s => s + 1)}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-medium text-sm transition-colors"
            >
              Avanti <ChevronRight size={16} />
            </button>
          )}
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-1.5 pb-5">
          {steps.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${i === step ? 'bg-violet-400' : 'bg-gray-700 hover:bg-gray-600'}`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default OnboardingTour
